import { useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { ToastNotification } from '@/components/ui/ToastNotification';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import {
  ArchiveIcon,
  EnvelopeIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';
import { ManageUsersContent } from '@/content/ManageUsersContent';
import {
  useUpdateUserRole,
  useDeleteUser,
  useUpdateUserEventVisibility,
} from '@/lib/hooks/useUserProfile';
import { useUserGroupMemberships } from '@/lib/hooks/useMemberships';
import { useAuth } from '@/lib/hooks/useAuth';
import type { UserProfile, UserRole } from '@/lib/types/database';
import { useRoleDirectory } from '@/lib/hooks/useRoleDirectory';
import { cn } from '@/lib/utils';
import { getGroupRoleTone } from '@/lib/utils/groups.utils';
import { getRoleLabelByKey } from '@/lib/utils/roles.utils';
import { formatMemberSinceDate } from '@/lib/utils/users.utils';
import { PreferenceToggle } from '@/components/ui/preference-toggle';
import { UserGroupsDrawer } from './UserGroupsDrawer';

export interface UserDetailsDrawerProps {
  /** Controls whether the drawer is open */
  open: boolean;
  /** Callback when the drawer open state changes */
  onOpenChange: (open: boolean) => void;
  /** The user to display */
  user: UserProfile | null;
}

/**
 * Drawer component that displays user information
 * Shows user details and action button placeholders
 */
export function UserDetailsDrawer({
  open,
  onOpenChange,
  user,
}: UserDetailsDrawerProps) {
  const { user: currentUser } = useAuth();
  const { toast, showToast, hideToast } = useToastNotification();
  const updateRoleMutation = useUpdateUserRole();
  const deleteUserMutation = useDeleteUser();
  const updateEventVisibilityMutation = useUpdateUserEventVisibility();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isRoleChangeOpen, setIsRoleChangeOpen] = useState(false);
  const [isGroupsDrawerOpen, setIsGroupsDrawerOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);

  const { data: assignedGroups = [], isLoading: isLoadingGroups } =
    useUserGroupMemberships(user?.id ?? null);

  // Available roles for selection, sourced from the admin-managed role list
  const { options: availableRoles } = useRoleDirectory();

  if (!user) {
    return null;
  }

  const userName = user.full_name || user.email || 'Unknown User';
  const isSelf = currentUser?.id === user.id;

  // Handle role change confirmation
  const handleRoleChangeConfirm = async () => {
    if (isSelf || !selectedRole) {
      return;
    }

    try {
      await updateRoleMutation.mutateAsync({
        userId: user.id,
        role: selectedRole,
      });
      setIsRoleChangeOpen(false);
      setSelectedRole(null);
      // Show success toast
      const roleDisplay = getRoleLabelByKey(selectedRole);
      showToast({
        title: 'Role updated',
        description: `${userName}'s role has been updated to ${roleDisplay}.`,
        variant: 'success',
      });
    } catch (error) {
      // Show error toast
      showToast({
        title: 'Update failed',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to update user role. Please try again.',
        variant: 'error',
      });
      console.error('Failed to update role:', error);
    }
  };

  // Open role change drawer
  const openRoleChangeDrawer = () => {
    setSelectedRole(user.role);
    setIsRoleChangeOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (isSelf) {
      return;
    }

    try {
      await deleteUserMutation.mutateAsync(user.id);
      setIsDeleteConfirmOpen(false);
      onOpenChange(false);
      // Show success toast
      showToast({
        title: 'User deleted',
        description: `${userName} has been deleted successfully.`,
        variant: 'success',
      });
    } catch (error) {
      // Show error toast
      showToast({
        title: 'Delete failed',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to delete user. Please try again.',
        variant: 'error',
      });
      console.error('Failed to delete user:', error);
    }
  };

  // Handle event visibility toggle
  const handleEventVisibilityToggle = async () => {
    if (isSelf) {
      return;
    }

    const newValue = !user.can_see_all_events;

    try {
      await updateEventVisibilityMutation.mutateAsync({
        userId: user.id,
        canSeeAllEvents: newValue,
      });
      // Show success toast
      showToast({
        title: 'Permission updated',
        description: `${userName} can now ${
          newValue ? 'see all events' : 'only see events based on their role'
        }.`,
        variant: 'success',
      });
    } catch (error) {
      // Show error toast
      showToast({
        title: 'Update failed',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to update event visibility permission. Please try again.',
        variant: 'error',
      });
      console.error('Failed to update event visibility:', error);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <div className="mx-auto w-full max-w-sm">
          <DrawerHeader>
            <DrawerTitle className="text-[24px] mb-3">{userName}</DrawerTitle>

            {/* User Info section */}
            <div className="flex flex-col bg-slate-600 rounded-xl p-4 gap-3 mb-3">
              {/* Email section */}
              {user.email && (
                <div>
                  <DrawerDescription className="text-small text-slate-400 font-semibold mb-1">
                    {ManageUsersContent.drawer.email}
                  </DrawerDescription>
                  <div className="flex items-center gap-2 text-body text-slate-50">
                    <EnvelopeIcon size={20} weight="regular" />
                    <span>{user.email}</span>
                  </div>
                </div>
              )}
              {/* Role section */}
              <div>
                <DrawerDescription className="text-small text-slate-400 font-semibold mb-1">
                  {ManageUsersContent.drawer.role}
                </DrawerDescription>
                <p className="text-body text-slate-50">
                  {user.role
                    ? getRoleLabelByKey(user.role)
                    : 'No role assigned'}
                </p>
              </div>

              {/* Groups section */}
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <DrawerDescription className="text-small text-slate-400 font-semibold">
                    {ManageUsersContent.groups.sectionTitle}
                  </DrawerDescription>
                  <span className="text-small text-slate-500">
                    {ManageUsersContent.groups.assignedCount(
                      assignedGroups.length
                    )}
                  </span>
                </div>
                {isLoadingGroups ? (
                  <p className="text-body text-slate-400">Loading…</p>
                ) : assignedGroups.length === 0 ? (
                  <p className="text-body text-slate-400">
                    {ManageUsersContent.groups.unassigned}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {assignedGroups.map((group) => {
                      const tone = getGroupRoleTone(
                        group.parent_role as UserRole
                      );
                      const isArchived = !!group.archived_at;
                      return (
                        <span
                          key={group.id}
                          className={cn(
                            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-field-42 text-[11px]',
                            isArchived
                              ? 'border-amber-400/40 bg-amber-400/15 text-amber-300'
                              : tone.pillClassName
                          )}
                        >
                          {isArchived && (
                            <ArchiveIcon size={10} weight="bold" />
                          )}
                          {group.display_name}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Member Since section */}
              <div>
                <DrawerDescription className="text-small text-slate-400 mb-1 font-semibold">
                  {ManageUsersContent.drawer.memberSince}
                </DrawerDescription>
                <p className="text-body text-slate-50">
                  {formatMemberSinceDate(user.created_at)}
                </p>
              </div>
            </div>

            {/* Permissions section */}
            {!isSelf && (
              <div className="mb-3">
                <PreferenceToggle
                  label={
                    ManageUsersContent.drawer.permissions.canSeeAllEvents.label
                  }
                  description={
                    ManageUsersContent.drawer.permissions.canSeeAllEvents
                      .description
                  }
                  checked={user.can_see_all_events ?? false}
                  disabled={updateEventVisibilityMutation.isPending}
                  onChange={handleEventVisibilityToggle}
                />
              </div>
            )}

            {/* Actions section */}
            {!isSelf && (
              <div className="mb-6">
                <DrawerDescription className="text-small text-slate-400 mb-2 font-semibold">
                  Actions
                </DrawerDescription>
                <div className="space-y-2">
                  <Button
                    variant="default"
                    className="w-full h-12 bg-sky-500"
                    onClick={openRoleChangeDrawer}
                    disabled={updateRoleMutation.isPending}
                  >
                    {ManageUsersContent.drawer.actions.changeRole}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full h-12"
                    onClick={() => setIsGroupsDrawerOpen(true)}
                  >
                    <PencilSimpleIcon size={16} weight="bold" />
                    {ManageUsersContent.groups.editAction}
                  </Button>
                  <Button
                    variant="destructive"
                    className="w-full h-12"
                    onClick={() => setIsDeleteConfirmOpen(true)}
                    disabled={deleteUserMutation.isPending}
                  >
                    {deleteUserMutation.isPending
                      ? 'Deleting...'
                      : ManageUsersContent.drawer.actions.deleteUser}
                  </Button>
                </div>
              </div>
            )}
            {isSelf && (
              <div className="mb-6">
                <DrawerDescription className="text-small text-slate-400 mb-2 font-semibold">
                  Actions
                </DrawerDescription>
                <p className="text-body text-slate-300">
                  {ManageUsersContent.drawer.actions.selfModificationWarning}
                </p>
              </div>
            )}
          </DrawerHeader>

          <DrawerFooter>
            <DrawerClose asChild>
              <Button variant="outline" className="h-16">
                Close
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>

      {/* Role Change Drawer */}
      <Drawer open={isRoleChangeOpen} onOpenChange={setIsRoleChangeOpen}>
        <DrawerContent>
          <div className="mx-auto w-full max-w-sm">
            <DrawerHeader>
              <DrawerTitle>
                {ManageUsersContent.drawer.roleChange.title}
              </DrawerTitle>
              <DrawerDescription>
                {ManageUsersContent.drawer.roleChange.description}
              </DrawerDescription>
            </DrawerHeader>
            <div className=" bg-slate-600 p-4 py-8 mb-4 rounded-lg">
              <div className="space-y-2">
                {availableRoles.map((role) => (
                  <Button
                    key={role.value}
                    variant={
                      selectedRole === role.value ? 'default' : 'outline'
                    }
                    className={cn(
                      'w-full h-12 justify-start',
                      selectedRole === role.value && 'bg-sky-500 text-slate-50'
                    )}
                    onClick={() => setSelectedRole(role.value)}
                  >
                    {role.label}
                  </Button>
                ))}
              </div>
            </div>
            <DrawerFooter>
              <Button
                size="lg"
                onClick={handleRoleChangeConfirm}
                disabled={
                  updateRoleMutation.isPending ||
                  !selectedRole ||
                  selectedRole === user.role
                }
              >
                {updateRoleMutation.isPending
                  ? 'Updating...'
                  : ManageUsersContent.drawer.roleChange.confirm}
              </Button>
              <DrawerClose asChild>
                <Button variant="outline" size="lg">
                  {ManageUsersContent.drawer.roleChange.cancel}
                </Button>
              </DrawerClose>
            </DrawerFooter>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Edit Groups Drawer */}
      <UserGroupsDrawer
        open={isGroupsDrawerOpen}
        onOpenChange={setIsGroupsDrawerOpen}
        user={user}
      />

      {/* Delete Confirmation Drawer */}
      <Drawer open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>
              {ManageUsersContent.drawer.delete.confirmation.title}
            </DrawerTitle>
            <DrawerDescription>
              {ManageUsersContent.drawer.delete.confirmation.description}
            </DrawerDescription>
          </DrawerHeader>
          <DrawerFooter>
            <Button
              variant="destructive"
              size="lg"
              onClick={handleDeleteConfirm}
              disabled={deleteUserMutation.isPending}
            >
              {ManageUsersContent.drawer.delete.confirmation.confirm}
            </Button>
            <DrawerClose asChild>
              <Button variant="outline" size="lg">
                {ManageUsersContent.drawer.delete.confirmation.cancel}
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Toast Notification */}
      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
      />
    </Drawer>
  );
}
